// Skill Types - Base skill categories determined by weapon
// Players create skills by selecting a type and adjusting parameters

import { ElementType } from '@shared/types';

// Weapon types that determine available skill types
export type WeaponType = 'kilic' | 'balta' | 'cekic' | 'hancer' | 'yay' | 'asa' | 'deger' | 'kalkan' | 'davul';

// Base skill type categories
export type SkillTypeId =
  // Melee types
  | 'slash'         // Kesme - Kılıç
  | 'cleave'        // Yarma - Balta
  | 'spin'          // Dönerek Saldırı - Kılıç, Balta
  | 'thrust'        // İleri Hamle - Kılıç, Mızrak
  | 'slam'          // Ezme - Çekiç, Balta
  | 'shockwave'     // Şok Dalgası - Çekiç
  | 'stab'          // Bıçaklama - Hançer
  | 'backstab'      // Sırttan Bıçaklama - Hançer
  // Ranged types
  | 'arrow'         // Ok - Yay
  | 'multishot'     // Çoklu Ok - Yay
  | 'rain'          // Yağmur - Yay
  // Magic types
  | 'projectile'    // Mermi - Asa, Değnek
  | 'beam'          // Işın - Asa
  | 'aoe'           // Alan Hasarı - Asa, Değnek
  | 'chain'         // Zincir - Değnek
  | 'explosion'     // Patlama - Değnek
  // Defensive types
  | 'bash'          // Çarpma - Kalkan
  | 'block_counter' // Blok Karşılığı - Kalkan
  | 'charge'        // Hücum - Kalkan
  // Support types
  | 'totem'         // Totem - Davul
  | 'summon';       // Çağırma - Davul

// Skill type definition
export interface SkillTypeDefinition {
  id: SkillTypeId;
  name: string;
  nameKey: string;
  description: string;
  descriptionKey: string;

  // Visual
  icon: string;
  color: string;

  // Base mechanics
  targetType: 'self' | 'enemy' | 'point' | 'direction';
  shape: 'single' | 'cone' | 'circle' | 'line' | 'projectile';
  defaultElement: ElementType;

  // Allowed weapons
  allowedWeapons: WeaponType[];

  // Base parameter ranges (min, max, default)
  baseParams: {
    damage: { min: number; max: number; default: number };
    areaRadius: { min: number; max: number; default: number };
    range: { min: number; max: number; default: number };
    projectileCount: { min: number; max: number; default: number };
    castSpeed: { min: number; max: number; default: number };
  };

  // Base costs
  baseCooldown: number;
  baseManaCost: number;
}

// All skill type definitions
export const SKILL_TYPES: Record<SkillTypeId, SkillTypeDefinition> = {
  // ========== MELEE TYPES ==========
  slash: {
    id: 'slash',
    name: 'Kesme',
    nameKey: 'skill_type.slash.name',
    description: 'Hızlı kesici saldırı.',
    descriptionKey: 'skill_type.slash.description',
    icon: 'slash',
    color: '#ff6b6b',
    targetType: 'direction',
    shape: 'cone',
    defaultElement: 'physical',
    allowedWeapons: ['kilic'],
    baseParams: {
      damage: { min: 0.5, max: 2.0, default: 1.0 },
      areaRadius: { min: 40, max: 120, default: 60 },
      range: { min: 40, max: 100, default: 60 },
      projectileCount: { min: 1, max: 1, default: 1 },
      castSpeed: { min: 0.5, max: 2.0, default: 1.2 },
    },
    baseCooldown: 0.3,
    baseManaCost: 5,
  },

  cleave: {
    id: 'cleave',
    name: 'Yarma',
    nameKey: 'skill_type.cleave.name',
    description: 'Geniş açılı güçlü saldırı.',
    descriptionKey: 'skill_type.cleave.description',
    icon: 'cleave',
    color: '#e74c3c',
    targetType: 'direction',
    shape: 'cone',
    defaultElement: 'physical',
    allowedWeapons: ['balta'],
    baseParams: {
      damage: { min: 0.8, max: 2.5, default: 1.3 },
      areaRadius: { min: 60, max: 150, default: 90 },
      range: { min: 50, max: 100, default: 70 },
      projectileCount: { min: 1, max: 1, default: 1 },
      castSpeed: { min: 0.4, max: 1.5, default: 0.8 },
    },
    baseCooldown: 0.8,
    baseManaCost: 10,
  },

  spin: {
    id: 'spin',
    name: 'Dönerek Saldırı',
    nameKey: 'skill_type.spin.name',
    description: 'Etrafındaki tüm düşmanlara dönerek saldırır.',
    descriptionKey: 'skill_type.spin.description',
    icon: 'spin',
    color: '#c0392b',
    targetType: 'self',
    shape: 'circle',
    defaultElement: 'physical',
    allowedWeapons: ['kilic', 'balta'],
    baseParams: {
      damage: { min: 0.4, max: 1.5, default: 0.8 },
      areaRadius: { min: 60, max: 200, default: 100 },
      range: { min: 0, max: 0, default: 0 },
      projectileCount: { min: 1, max: 1, default: 1 },
      castSpeed: { min: 0.5, max: 1.5, default: 1.0 },
    },
    baseCooldown: 3.0,
    baseManaCost: 20,
  },

  thrust: {
    id: 'thrust',
    name: 'İleri Hamle',
    nameKey: 'skill_type.thrust.name',
    description: 'Düşmana doğru atılarak saldırı.',
    descriptionKey: 'skill_type.thrust.description',
    icon: 'thrust',
    color: '#e67e22',
    targetType: 'direction',
    shape: 'line',
    defaultElement: 'physical',
    allowedWeapons: ['kilic'],
    baseParams: {
      damage: { min: 0.8, max: 2.0, default: 1.2 },
      areaRadius: { min: 20, max: 60, default: 30 },
      range: { min: 100, max: 300, default: 150 },
      projectileCount: { min: 1, max: 1, default: 1 },
      castSpeed: { min: 1.0, max: 3.0, default: 2.0 },
    },
    baseCooldown: 4.0,
    baseManaCost: 15,
  },

  slam: {
    id: 'slam',
    name: 'Ezme',
    nameKey: 'skill_type.slam.name',
    description: 'Yere güçlü bir vuruş.',
    descriptionKey: 'skill_type.slam.description',
    icon: 'slam',
    color: '#8e44ad',
    targetType: 'point',
    shape: 'circle',
    defaultElement: 'physical',
    allowedWeapons: ['cekic', 'balta'],
    baseParams: {
      damage: { min: 1.0, max: 3.0, default: 1.8 },
      areaRadius: { min: 60, max: 180, default: 100 },
      range: { min: 40, max: 100, default: 60 },
      projectileCount: { min: 1, max: 1, default: 1 },
      castSpeed: { min: 0.3, max: 1.0, default: 0.6 },
    },
    baseCooldown: 5.0,
    baseManaCost: 25,
  },

  shockwave: {
    id: 'shockwave',
    name: 'Şok Dalgası',
    nameKey: 'skill_type.shockwave.name',
    description: 'Yayılan şok dalgası.',
    descriptionKey: 'skill_type.shockwave.description',
    icon: 'shockwave',
    color: '#9b59b6',
    targetType: 'direction',
    shape: 'cone',
    defaultElement: 'physical',
    allowedWeapons: ['cekic'],
    baseParams: {
      damage: { min: 0.6, max: 2.0, default: 1.0 },
      areaRadius: { min: 80, max: 250, default: 150 },
      range: { min: 100, max: 300, default: 200 },
      projectileCount: { min: 1, max: 1, default: 1 },
      castSpeed: { min: 0.4, max: 1.2, default: 0.7 },
    },
    baseCooldown: 6.0,
    baseManaCost: 30,
  },

  stab: {
    id: 'stab',
    name: 'Bıçaklama',
    nameKey: 'skill_type.stab.name',
    description: 'Hızlı bıçak saldırısı.',
    descriptionKey: 'skill_type.stab.description',
    icon: 'stab',
    color: '#2ecc71',
    targetType: 'direction',
    shape: 'single',
    defaultElement: 'physical',
    allowedWeapons: ['hancer'],
    baseParams: {
      damage: { min: 0.6, max: 1.8, default: 1.0 },
      areaRadius: { min: 0, max: 0, default: 0 },
      range: { min: 30, max: 60, default: 40 },
      projectileCount: { min: 1, max: 5, default: 2 },
      castSpeed: { min: 1.5, max: 3.0, default: 2.0 },
    },
    baseCooldown: 0.2,
    baseManaCost: 3,
  },

  backstab: {
    id: 'backstab',
    name: 'Sırttan Bıçaklama',
    nameKey: 'skill_type.backstab.name',
    description: 'Arkadan kritik hasar.',
    descriptionKey: 'skill_type.backstab.description',
    icon: 'backstab',
    color: '#27ae60',
    targetType: 'enemy',
    shape: 'single',
    defaultElement: 'physical',
    allowedWeapons: ['hancer'],
    baseParams: {
      damage: { min: 2.0, max: 5.0, default: 3.0 },
      areaRadius: { min: 0, max: 0, default: 0 },
      range: { min: 30, max: 60, default: 40 },
      projectileCount: { min: 1, max: 1, default: 1 },
      castSpeed: { min: 0.5, max: 1.5, default: 1.0 },
    },
    baseCooldown: 8.0,
    baseManaCost: 20,
  },

  // ========== RANGED TYPES ==========
  arrow: {
    id: 'arrow',
    name: 'Ok Atışı',
    nameKey: 'skill_type.arrow.name',
    description: 'Temel ok atışı.',
    descriptionKey: 'skill_type.arrow.description',
    icon: 'arrow',
    color: '#3498db',
    targetType: 'direction',
    shape: 'projectile',
    defaultElement: 'physical',
    allowedWeapons: ['yay'],
    baseParams: {
      damage: { min: 0.5, max: 2.0, default: 1.0 },
      areaRadius: { min: 0, max: 30, default: 0 },
      range: { min: 200, max: 500, default: 350 },
      projectileCount: { min: 1, max: 3, default: 1 },
      castSpeed: { min: 0.8, max: 2.0, default: 1.2 },
    },
    baseCooldown: 0.4,
    baseManaCost: 5,
  },

  multishot: {
    id: 'multishot',
    name: 'Çoklu Ok',
    nameKey: 'skill_type.multishot.name',
    description: 'Birden fazla ok fırlatır.',
    descriptionKey: 'skill_type.multishot.description',
    icon: 'multishot',
    color: '#2980b9',
    targetType: 'direction',
    shape: 'cone',
    defaultElement: 'physical',
    allowedWeapons: ['yay'],
    baseParams: {
      damage: { min: 0.3, max: 1.0, default: 0.5 },
      areaRadius: { min: 60, max: 150, default: 90 },
      range: { min: 200, max: 450, default: 300 },
      projectileCount: { min: 3, max: 10, default: 5 },
      castSpeed: { min: 0.6, max: 1.5, default: 0.9 },
    },
    baseCooldown: 2.0,
    baseManaCost: 18,
  },

  rain: {
    id: 'rain',
    name: 'Ok Yağmuru',
    nameKey: 'skill_type.rain.name',
    description: 'Belirli bir alana ok yağdırır.',
    descriptionKey: 'skill_type.rain.description',
    icon: 'rain',
    color: '#1abc9c',
    targetType: 'point',
    shape: 'circle',
    defaultElement: 'physical',
    allowedWeapons: ['yay'],
    baseParams: {
      damage: { min: 0.2, max: 0.8, default: 0.4 },
      areaRadius: { min: 80, max: 250, default: 150 },
      range: { min: 200, max: 500, default: 350 },
      projectileCount: { min: 5, max: 20, default: 10 },
      castSpeed: { min: 0.3, max: 1.0, default: 0.5 },
    },
    baseCooldown: 8.0,
    baseManaCost: 35,
  },

  // ========== MAGIC TYPES ==========
  projectile: {
    id: 'projectile',
    name: 'Enerji Mermisi',
    nameKey: 'skill_type.projectile.name',
    description: 'Büyülü enerji mermisi fırlatır.',
    descriptionKey: 'skill_type.projectile.description',
    icon: 'projectile',
    color: '#9b59b6',
    targetType: 'direction',
    shape: 'projectile',
    defaultElement: 'karanlik',
    allowedWeapons: ['asa', 'deger'],
    baseParams: {
      damage: { min: 0.6, max: 2.0, default: 1.0 },
      areaRadius: { min: 0, max: 50, default: 0 },
      range: { min: 200, max: 500, default: 300 },
      projectileCount: { min: 1, max: 5, default: 1 },
      castSpeed: { min: 0.8, max: 2.0, default: 1.0 },
    },
    baseCooldown: 0.5,
    baseManaCost: 8,
  },

  beam: {
    id: 'beam',
    name: 'Enerji Işını',
    nameKey: 'skill_type.beam.name',
    description: 'Sürekli enerji ışını.',
    descriptionKey: 'skill_type.beam.description',
    icon: 'beam',
    color: '#8e44ad',
    targetType: 'direction',
    shape: 'line',
    defaultElement: 'od',
    allowedWeapons: ['asa'],
    baseParams: {
      damage: { min: 0.3, max: 1.0, default: 0.5 },
      areaRadius: { min: 20, max: 60, default: 30 },
      range: { min: 150, max: 400, default: 250 },
      projectileCount: { min: 1, max: 1, default: 1 },
      castSpeed: { min: 0.5, max: 1.5, default: 1.0 },
    },
    baseCooldown: 3.0,
    baseManaCost: 25,
  },

  aoe: {
    id: 'aoe',
    name: 'Alan Patlaması',
    nameKey: 'skill_type.aoe.name',
    description: 'Belirli bir alanda hasar verir.',
    descriptionKey: 'skill_type.aoe.description',
    icon: 'aoe',
    color: '#e74c3c',
    targetType: 'point',
    shape: 'circle',
    defaultElement: 'od',
    allowedWeapons: ['asa', 'deger'],
    baseParams: {
      damage: { min: 0.8, max: 2.5, default: 1.5 },
      areaRadius: { min: 80, max: 250, default: 150 },
      range: { min: 150, max: 400, default: 250 },
      projectileCount: { min: 1, max: 1, default: 1 },
      castSpeed: { min: 0.3, max: 1.0, default: 0.6 },
    },
    baseCooldown: 5.0,
    baseManaCost: 30,
  },

  chain: {
    id: 'chain',
    name: 'Zincir Büyü',
    nameKey: 'skill_type.chain.name',
    description: 'Düşmanlar arasında sıçrayan büyü.',
    descriptionKey: 'skill_type.chain.description',
    icon: 'chain',
    color: '#3498db',
    targetType: 'enemy',
    shape: 'projectile',
    defaultElement: 'gok',
    allowedWeapons: ['deger'],
    baseParams: {
      damage: { min: 0.5, max: 1.5, default: 0.8 },
      areaRadius: { min: 0, max: 0, default: 0 },
      range: { min: 150, max: 350, default: 250 },
      projectileCount: { min: 2, max: 7, default: 3 },
      castSpeed: { min: 0.8, max: 1.5, default: 1.0 },
    },
    baseCooldown: 3.0,
    baseManaCost: 22,
  },

  explosion: {
    id: 'explosion',
    name: 'Patlama',
    nameKey: 'skill_type.explosion.name',
    description: 'Güçlü bir patlama yaratır.',
    descriptionKey: 'skill_type.explosion.description',
    icon: 'explosion',
    color: '#e67e22',
    targetType: 'point',
    shape: 'circle',
    defaultElement: 'od',
    allowedWeapons: ['deger'],
    baseParams: {
      damage: { min: 1.5, max: 4.0, default: 2.5 },
      areaRadius: { min: 100, max: 300, default: 180 },
      range: { min: 100, max: 300, default: 200 },
      projectileCount: { min: 1, max: 1, default: 1 },
      castSpeed: { min: 0.2, max: 0.8, default: 0.4 },
    },
    baseCooldown: 8.0,
    baseManaCost: 45,
  },

  // ========== DEFENSIVE TYPES ==========
  bash: {
    id: 'bash',
    name: 'Kalkan Çarpması',
    nameKey: 'skill_type.bash.name',
    description: 'Kalkanla vurarak düşmanı sersemletir.',
    descriptionKey: 'skill_type.bash.description',
    icon: 'bash',
    color: '#7f8c8d',
    targetType: 'direction',
    shape: 'cone',
    defaultElement: 'physical',
    allowedWeapons: ['kalkan'],
    baseParams: {
      damage: { min: 0.5, max: 1.5, default: 0.8 },
      areaRadius: { min: 40, max: 100, default: 60 },
      range: { min: 30, max: 70, default: 50 },
      projectileCount: { min: 1, max: 1, default: 1 },
      castSpeed: { min: 1.0, max: 2.0, default: 1.5 },
    },
    baseCooldown: 4.0,
    baseManaCost: 15,
  },

  block_counter: {
    id: 'block_counter',
    name: 'Blok Karşılığı',
    nameKey: 'skill_type.block_counter.name',
    description: 'Bloktan sonra karşı saldırı.',
    descriptionKey: 'skill_type.block_counter.description',
    icon: 'block_counter',
    color: '#95a5a6',
    targetType: 'enemy',
    shape: 'single',
    defaultElement: 'physical',
    allowedWeapons: ['kalkan'],
    baseParams: {
      damage: { min: 1.5, max: 3.5, default: 2.0 },
      areaRadius: { min: 0, max: 0, default: 0 },
      range: { min: 40, max: 80, default: 60 },
      projectileCount: { min: 1, max: 1, default: 1 },
      castSpeed: { min: 1.5, max: 3.0, default: 2.0 },
    },
    baseCooldown: 6.0,
    baseManaCost: 20,
  },

  charge: {
    id: 'charge',
    name: 'Kalkan Hücumu',
    nameKey: 'skill_type.charge.name',
    description: 'Kalkanla ileri atılarak çarpar.',
    descriptionKey: 'skill_type.charge.description',
    icon: 'charge',
    color: '#34495e',
    targetType: 'direction',
    shape: 'line',
    defaultElement: 'physical',
    allowedWeapons: ['kalkan'],
    baseParams: {
      damage: { min: 1.0, max: 2.5, default: 1.5 },
      areaRadius: { min: 30, max: 80, default: 50 },
      range: { min: 100, max: 300, default: 200 },
      projectileCount: { min: 1, max: 1, default: 1 },
      castSpeed: { min: 1.5, max: 3.0, default: 2.5 },
    },
    baseCooldown: 8.0,
    baseManaCost: 25,
  },

  // ========== SUPPORT TYPES ==========
  totem: {
    id: 'totem',
    name: 'Totem',
    nameKey: 'skill_type.totem.name',
    description: 'Düşmanlara hasar veren veya müttefikleri iyileştiren totem.',
    descriptionKey: 'skill_type.totem.description',
    icon: 'totem',
    color: '#16a085',
    targetType: 'point',
    shape: 'circle',
    defaultElement: 'gok',
    allowedWeapons: ['davul'],
    baseParams: {
      damage: { min: 0.2, max: 1.0, default: 0.5 },
      areaRadius: { min: 100, max: 250, default: 150 },
      range: { min: 100, max: 300, default: 200 },
      projectileCount: { min: 1, max: 1, default: 1 },
      castSpeed: { min: 0.5, max: 1.5, default: 0.8 },
    },
    baseCooldown: 12.0,
    baseManaCost: 40,
  },

  summon: {
    id: 'summon',
    name: 'Ruh Çağırma',
    nameKey: 'skill_type.summon.name',
    description: 'Savaşan bir ruh çağırır.',
    descriptionKey: 'skill_type.summon.description',
    icon: 'summon',
    color: '#1abc9c',
    targetType: 'point',
    shape: 'single',
    defaultElement: 'karanlik',
    allowedWeapons: ['davul'],
    baseParams: {
      damage: { min: 0.3, max: 1.0, default: 0.5 },
      areaRadius: { min: 0, max: 0, default: 0 },
      range: { min: 50, max: 200, default: 100 },
      projectileCount: { min: 1, max: 3, default: 1 },
      castSpeed: { min: 0.3, max: 1.0, default: 0.5 },
    },
    baseCooldown: 15.0,
    baseManaCost: 50,
  },
};

// Get available skill types for a weapon
export function getSkillTypesForWeapon(weapon: WeaponType): SkillTypeDefinition[] {
  return Object.values(SKILL_TYPES).filter(st => st.allowedWeapons.includes(weapon));
}

// Get skill type by ID
export function getSkillType(id: SkillTypeId): SkillTypeDefinition | undefined {
  return SKILL_TYPES[id];
}

// Map item baseId (or baseName) to WeaponType for skill validation
const WEAPON_BASE_MAP: Record<string, WeaponType> = {
  // By baseId
  'kilic': 'kilic', 'uzun_kilic': 'kilic',
  'balta': 'balta', 'savas_baltasi': 'balta',
  'yay': 'yay', 'kompozit_yay': 'yay',
  'asa': 'asa', 'kam_asasi': 'asa',
  'hancer': 'hancer',
  'cekic': 'cekic',
  'deger': 'deger',
  'kalkan': 'kalkan',
  'davul': 'davul',
  // By baseName (Turkish display name)
  'Kılıç': 'kilic', 'Uzun Kılıç': 'kilic',
  'Balta': 'balta', 'Savaş Baltası': 'balta',
  'Yay': 'yay', 'Kompozit Yay': 'yay',
  'Asa': 'asa', 'Kam Asası': 'asa',
  'Hançer': 'hancer',
  'Çekiç': 'cekic',
  'Değnek': 'deger',
  'Kalkan': 'kalkan',
  'Davul': 'davul',
};

/** Get WeaponType from an item's baseName or name */
export function getWeaponTypeFromName(name: string | undefined): WeaponType | null {
  if (!name) return null;
  return WEAPON_BASE_MAP[name] ?? null;
}

/** Check if a V1 skill is allowed with the given weapon type */
export function isSkillAllowedForWeapon(skillTypeId: SkillTypeId, weaponType: WeaponType | null): boolean {
  if (!weaponType) return true; // No weapon = allow all (unarmed)
  const skillType = SKILL_TYPES[skillTypeId];
  if (!skillType) return true;
  return skillType.allowedWeapons.includes(weaponType);
}
